import { Column } from '@etg/wings';
import type { MutableRefObject } from 'react';
import { useRef, useState } from 'react';
import { price as priceUtil, Profiler } from '@eti/utils';
import { cabinBaggage, checkInBaggage } from '@eti/products';
import { useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import type {
  AncillaryBundlesOnTdPageFragment,
  CabinBaggageTripSummary,
  CheckedBaggageSummary,
  PersonalItemTripSummary,
  TripCharacteristics,
  TripType,
} from '@eti/schema-types';
import Media from 'react-media';
import type { FormStateMap } from 'redux-form';
import { formValueSelector } from 'redux-form';
import type { MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import UiCart from '../../../widgets/cart/components/UiCart';
import StickySidebar from '../../../widgets/sidebar/components/StickySidebar';
import {
  getAddedExtraProducts,
  getExtraProductsTotalPrice,
} from '../../../utils/addedExtraProducts';
import { getAddedAncillaryBundles } from '../../../utils/addedAncillaryBundles';
import { TRAVELER_DETAILS } from '../../../constants/formNamesConstants';
import CartContactInfo from '../../../widgets/cart/components/CartContactInfo';
import useIncludedOrUnavailableCheckInBaggage from '../../../widgets/cart/hooks/useIncludedOrUnavailableCheckInBaggage';
import useIncludedOrUnavailableCabinBaggage from '../../../widgets/cart/hooks/useIncludedOrUnavailableCabinBaggage';
import useIncludedOrUnavailablePersonalItem from '../../../widgets/cart/hooks/useIncludedOrUnavailablePersonalItem';
import useSelectedCheckInBaggage from '../utils/useSelectedCheckInBaggage';
import useSelectedCabinBaggagePerTraveler from '../utils/useSelectedCabinBaggagePerTraveler';
import {
  getMinNumberOfSeats,
  shouldShowSeatsLeftNotification,
} from '../../result/utils/resultUtils';
import type {
  BoundsType,
  TransformedCartTravelerPriceType,
  TransformedTDCartPaymentMethodPriceType,
  TransformedTDFormContainerSelectedPaymentMethodType,
} from '../../../common/types/transformations/models';
import type { ProductCartNamesType } from '../../../common/custom-hooks/productCartNames';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../utils/carriersUtils';
import CFCModal from '../../../common/cfc-modal/CFCModal';
import CFCInfoCard from '../../../common/cfc-info-card/CFCInfoCard';
import { AGENCY_FORM_SECTION, agencyFields } from '../constants/formFields';
import type { AgencyContactInfoType, Country } from '../models';
import MobileCart from './mobile-cart/MobileCart';

const { AGENT_FIRST_NAME, AGENT_LAST_NAME, AGENT_EMAIL, AGENT_PHONE, AGENT_PHONE_COUNTRY_ID } =
  agencyFields;

interface State {
  form: FormStateMap;
}

interface ConnectedProps {
  agencyContactInfo: AgencyContactInfoType;
  discountAmount: number;
  products: any;
  bundles: any;
  subTotalPrice: number;
  totalPrice: number;
}

interface TravelerDetailsCartMapStateProps {
  addedProductsNumber: number;
  bottomContentRef: MutableRefObject<HTMLDivElement | null>;
  bounds: BoundsType;
  bundleData: AncillaryBundlesOnTdPageFragment;
  bundleDataTDPage: AncillaryBundlesOnTdPageFragment;
  cabinBaggageTripSummary: CabinBaggageTripSummary;
  chargingCurrency?: any;
  checkedBaggageSummary: CheckedBaggageSummary;
  countries?: Country[];
  discountAmount: number;
  headerAndInfoHeight: number;
  isAncillaryBundlingEnabled: boolean;
  isMultiStop: boolean;
  isOnSectionOne?: boolean;
  marketingCarrierNames: string;
  numberOfTravelers: number;
  paymentMethodPrices: TransformedTDCartPaymentMethodPriceType[];
  personalItemTripSummary: PersonalItemTripSummary;
  productNames: ProductCartNamesType;
  selectedPaymentMethod: TransformedTDFormContainerSelectedPaymentMethodType;
  shareableUrl: string;
  totalTripPrice: number;
  travelerFormProducts: any[];
  travelerPrices: TransformedCartTravelerPriceType[];
  tripCharacteristics: TripCharacteristics[];
  tripProviderSystems?: string[];
  type: TripType;
}

type TravelerDetailsCartProps = Omit<
  TravelerDetailsCartMapStateProps,
  'bundleDataTDPage' | 'isAncillaryBundlingEnabled' | 'productNames' | 'totalTripPrice'
>;

type Props = TravelerDetailsCartProps & ConnectedProps;

const mapStateToProps: MapStateToProps<ConnectedProps, TravelerDetailsCartMapStateProps, State> = (
  state: State,
  {
    bundleDataTDPage,
    discountAmount = 0,
    isAncillaryBundlingEnabled,
    productNames,
    selectedPaymentMethod,
    totalTripPrice,
  }: TravelerDetailsCartMapStateProps,
) => {
  const extraProductsTotalPrice = getExtraProductsTotalPrice(TRAVELER_DETAILS)(state);
  const { bundles, bundlesTotalPrice: ancillaryBundlesTotalPrice } = getAddedAncillaryBundles(
    TRAVELER_DETAILS,
    bundleDataTDPage,
    isAncillaryBundlingEnabled,
  )(state);

  const subTotalPrice = Number(
    totalTripPrice + extraProductsTotalPrice + ancillaryBundlesTotalPrice,
  );
  const paymentDiscount = selectedPaymentMethod?.price < 0 ? selectedPaymentMethod?.price : 0;

  return {
    agencyContactInfo: formValueSelector(TRAVELER_DETAILS)(state, AGENCY_FORM_SECTION),
    discountAmount,
    products: getAddedExtraProducts(TRAVELER_DETAILS, productNames)(state),
    bundles,
    subTotalPrice,
    totalPrice: subTotalPrice + paymentDiscount - discountAmount,
  };
};

const getDesktopCartStyles = (bottomPadding?: number) => css`
  padding-bottom: ${bottomPadding ?? '0'}px;
`;

const titledCardStyles = css`
  margin-top: 8px;
`;

const TravelerDetailsCart = ({
  addedProductsNumber,
  agencyContactInfo,
  bottomContentRef,
  bounds,
  bundleData,
  bundles,
  cabinBaggageTripSummary,
  checkedBaggageSummary,
  countries = [],
  discountAmount,
  headerAndInfoHeight,
  isMultiStop,
  isOnSectionOne,
  marketingCarrierNames,
  numberOfTravelers,
  paymentMethodPrices,
  personalItemTripSummary,
  products,
  selectedPaymentMethod,
  shareableUrl,
  subTotalPrice,
  totalPrice,
  travelerFormProducts = [],
  travelerPrices,
  tripCharacteristics,
  tripProviderSystems,
  type,
  chargingCurrency,
}: Props) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [isCFCModalOpen, setIsCFCModalOpen] = useState(false);

  const includedOrUnavailableCheckInBaggage = useIncludedOrUnavailableCheckInBaggage(
    travelerPrices,
    checkedBaggageSummary,
    bundleData,
    checkInBaggage.id,
    products,
  );
  const includedOrUnavailableCabinBaggage = useIncludedOrUnavailableCabinBaggage(
    travelerPrices,
    cabinBaggageTripSummary,
    bundleData,
    cabinBaggage.id,
  );
  const includedOrUnavailablePersonalItem = useIncludedOrUnavailablePersonalItem(
    personalItemTripSummary,
    travelerPrices,
  );
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();

  const bundleContents = bundleData?.options?.find(
    (option) => option?.id === bundleData?.selectedOption,
  );

  const bundleBaggageDetails = bundleContents?.content?.find(
    (bundleContent) => bundleContent?.productTypeId === checkInBaggage.id.toString(),
  );

  const bundleCabinBaggageDetails = bundleContents?.content?.find(
    (bundleContent) => bundleContent?.productTypeId === cabinBaggage.id.toString(),
  );

  const selectedCheckInBaggage = useSelectedCheckInBaggage(travelerPrices, products);
  const selectedCabinBaggage = useSelectedCabinBaggagePerTraveler(
    travelerPrices,
    products,
    travelerFormProducts,
  );

  const cabinBaggageSelection = includedOrUnavailableCabinBaggage || selectedCabinBaggage;

  const checkedBaggageSelection = includedOrUnavailableCheckInBaggage || selectedCheckInBaggage;

  const bottomContentPadding = bottomContentRef?.current?.getBoundingClientRect().height;
  const isCFCEnabled = p('IbeClient.CarrierFlightConfirmation.Enabled');
  const seatsThreshold = p('Air.Result.ShowNumberOfSeatsLeft.WhenLessThan');
  const numberOfSeatsLeft = getMinNumberOfSeats(bounds);
  const shouldShowSeatsLeftNotificationMessage = shouldShowSeatsLeftNotification(
    numberOfSeatsLeft,
    numberOfTravelers,
    seatsThreshold,
  );
  // TODO: When totalPrice is changed to  a non null value for the UiCart in the mobile view, we move the below logic inside UiCart and remove the 'priceToDisplay' prop. See WEB-6144.
  const priceToDisplay = priceUtil.formatPrice(totalPrice);

  const isCabinBaggagePresent =
    (selectedCabinBaggage &&
      selectedCabinBaggage.length &&
      !selectedCabinBaggage[0].noBaggageSelected) ||
    (includedOrUnavailableCabinBaggage &&
      Boolean(includedOrUnavailableCabinBaggage.length) &&
      !includedOrUnavailableCabinBaggage[0].noBaggageAvailable);

  const isCheckedBaggagePresent =
    (selectedCheckInBaggage &&
      Boolean(selectedCheckInBaggage.length) &&
      !selectedCheckInBaggage[0].noBaggageSelected) ||
    (includedOrUnavailableCheckInBaggage &&
      Boolean(includedOrUnavailableCheckInBaggage.length) &&
      !includedOrUnavailableCheckInBaggage[0].noBaggageAvailable);

  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );

  const handleSendTrackEvent = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Traveler Details',
        action: 'Click',
        label: 'Travel details button',
      });
    }
  };

  const agentPhoneCountryId =
    agencyContactInfo && agencyContactInfo[AGENT_PHONE_COUNTRY_ID]
      ? agencyContactInfo[AGENT_PHONE_COUNTRY_ID]
      : null;
  const countryCode = countries.find((country) => country.id === agentPhoneCountryId)
    ?.phoneCountryPrefix;

  const contactInformation = agencyContactInfo ? (
    <CartContactInfo
      id="cart-contactInformation"
      label={t('AffiliateTravelAgency.Column.AgentInformation.Label')}
    >
      <CartContactInfo.Row
        content={`${agencyContactInfo[agencyFields.NAME]} – ${
          agencyContactInfo[agencyFields.OFFICE_ID]
        }`}
      />
      {agencyContactInfo[AGENT_FIRST_NAME] && agencyContactInfo[AGENT_LAST_NAME] && (
        <CartContactInfo.Row
          content={`${agencyContactInfo[AGENT_FIRST_NAME]} ${agencyContactInfo[AGENT_LAST_NAME]}`}
        />
      )}
      {agencyContactInfo[AGENT_EMAIL] && (
        <CartContactInfo.Row content={agencyContactInfo[AGENT_EMAIL]} />
      )}
      {agencyContactInfo[AGENT_PHONE] && countryCode && (
        <CartContactInfo.Row content={`+${countryCode} ${agencyContactInfo[AGENT_PHONE]}`} />
      )}
    </CartContactInfo>
  ) : undefined;

  const handleOpenCFCModal = () => {
    setIsCFCModalOpen(true);
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: isOnSectionOne ? 'TD page' : 'Extra Services',
        action: 'Click',
        label: 'Booking process information',
        property: 'Ryanair',
      });
    }
  };

  return (
    <Media query={mediaQueries.large.up}>
      {(matches) =>
        matches ? (
          <Column
            className={getDesktopCartStyles(bottomContentPadding)}
            ref={sidebarRef}
            width="2/6"
          >
            <StickySidebar containerRef={sidebarRef} topSpacing={headerAndInfoHeight}>
              <UiCart
                bounds={bounds}
                bundleBaggageDetails={bundleBaggageDetails}
                bundleCabinBaggageDetails={bundleCabinBaggageDetails}
                bundles={bundles}
                cabinBaggageSelection={cabinBaggageSelection}
                chargingCurrency={chargingCurrency}
                checkedBaggageSelection={checkedBaggageSelection}
                contactInformation={contactInformation}
                dataTestId="travelerDetails-cart"
                discountAmount={discountAmount}
                handleSendTrackEvent={handleSendTrackEvent}
                isCabinBaggagePresent={isCabinBaggagePresent}
                isCheckedBaggagePresent={isCheckedBaggagePresent}
                isMultiStop={isMultiStop}
                marketingCarrierNames={marketingCarrierNames}
                numberOfSeatsLeft={numberOfSeatsLeft}
                paymentMethods={paymentMethodPrices}
                personalItem={includedOrUnavailablePersonalItem}
                priceToDisplay={priceToDisplay}
                products={products}
                providerSystems={tripProviderSystems}
                selectedPaymentMethod={selectedPaymentMethod}
                shareableUrl={p('Result.ShareTrip.Enabled') ? shareableUrl : undefined}
                shouldShowSeatsLeftNotificationMessage={shouldShowSeatsLeftNotificationMessage}
                subTotalPrice={subTotalPrice}
                title={t('Cart.Title')}
                totalPrice={totalPrice}
                travelerPrices={travelerPrices}
                // @ts-ignore - WEBTECH-1361
                tripCharacteristics={tripCharacteristics}
                type={type}
              />
              {isCFCEnabled && isRyanAirTicketingOrMarketingCarrierPresent && (
                <>
                  <CFCInfoCard
                    className={titledCardStyles}
                    contentText={t('CarrierFlightConfirmation.InfoBox.Description')}
                    handleLinkButtonClick={handleOpenCFCModal}
                    linkButtonText={t('CarrierFlightConfirmation.InfoBox.AnchorButton.Text')}
                    title={t('CarrierFlightConfirmation.InfoBox.Title')}
                  />
                  <CFCModal
                    isOpen={isCFCModalOpen}
                    setIsOpen={setIsCFCModalOpen}
                    shouldShowProceedButton={false}
                  />
                </>
              )}
            </StickySidebar>
          </Column>
        ) : (
          <Profiler id="TravelerDetailsPage-Cart">
            <MobileCart addedProductsNumber={addedProductsNumber} totalPrice={totalPrice}>
              <UiCart
                bounds={bounds}
                bundleBaggageDetails={bundleBaggageDetails}
                bundleCabinBaggageDetails={bundleCabinBaggageDetails}
                bundles={bundles}
                cabinBaggageSelection={cabinBaggageSelection}
                chargingCurrency={chargingCurrency}
                checkedBaggageSelection={
                  includedOrUnavailableCheckInBaggage || selectedCheckInBaggage
                }
                contactInformation={contactInformation}
                dataTestId="mobileCart"
                discountAmount={discountAmount}
                handleSendTrackEvent={handleSendTrackEvent}
                isCabinBaggagePresent={isCabinBaggagePresent}
                isCheckedBaggagePresent={isCheckedBaggagePresent}
                isMultiStop={isMultiStop}
                marketingCarrierNames={marketingCarrierNames}
                numberOfSeatsLeft={numberOfSeatsLeft}
                paymentMethods={paymentMethodPrices}
                personalItem={includedOrUnavailablePersonalItem}
                priceToDisplay={priceToDisplay}
                products={products}
                selectedPaymentMethod={selectedPaymentMethod}
                shareableUrl={p('Result.ShareTrip.Enabled') ? shareableUrl : undefined}
                shouldShowSeatsLeftNotificationMessage={shouldShowSeatsLeftNotificationMessage}
                subTotalPrice={subTotalPrice}
                totalPrice={totalPrice}
                travelerPrices={travelerPrices}
                tripCharacteristics={tripCharacteristics}
                type={type}
              />
            </MobileCart>
          </Profiler>
        )
      }
    </Media>
  );
};

export default connect(mapStateToProps, null)(TravelerDetailsCart);
